// A token for a site character (issue 848), the client's half: the DM's list
// of the characters placed at this table (in the Library panel), a token's
// character in the Properties panel — linked, refreshed from the sheet,
// unlinked — and the way to the site's own page for the sheet. Since issue
// 1579 the list is grouped under the characters' parties, and a character
// token's Properties names its party, read from its character when it is
// shown (nothing about a party is stored on the token). The server
// reads the sheet; nothing here computes a number. A refusal, or the
// server's notice that the sheet could not be read, is said in the table's
// notice as it came.
import { useCallback, useEffect, useState } from 'react';
import { EMPTY_GRID, boardExtent, byParty, partyWords, type TableObject, type Values } from '@dragons-table/vtt-shared';
import { NOT_ON_SHEET, acceptCharacterHp, characterPageUrl, listCompanions, placeCompanion, type TableCompanion, hpOffSheetOf, sheetWindowName, linkCharacter, listTableCharacters, placeCharacter, refreshCharacter, type CharacterAnswer, type TableCharacter } from '../../characters';
import { boardBus } from '../../board/bus';
import { placeSpot } from '../../place';
import { useBoardMap } from '../../board/sceneMap';
import { useTableState } from '../../table/TableState';
import { useHolds } from '../../table/mayAct';
import { useShell } from '../context';
import { ReplaceQuestion, typedNumbers, type LinkMode } from './EntryParts';
import { useNumberWords } from '../../table/numberWords';
import { BTN } from '../ui';

const btn = BTN;

/** The character a thing in this person's projection stands for, or null (absent for anyone but the DM and its owners). */
export const characterOf = (o: unknown): string | null => {
  const c = (o as { character?: unknown } | null)?.character;
  return typeof c === 'string' && c ? c : null;
};

/** How often the DM's list is read again while it is open and the window is in view (issue 1551). */
export const TABLE_CHARACTERS_EVERY_MS = 20_000;

/**
 * The DM's list of the table's characters, read from the server; `reload` after anything that changes it. A player
 * places or takes off a character on the site, which tells the table nothing (issue 1551), so the list is also read
 * again when someone arrives or leaves, when the window comes back into view, and every 20 seconds while it is in view.
 */
export function useTableCharacters(enabled: boolean): { list: TableCharacter[] | null; failed: boolean; reload: () => Promise<void> } {
  const { campaignId, table } = useShell();
  const { scene } = useTableState();
  const sceneId = scene?.id ?? null;
  const [list, setList] = useState<TableCharacter[] | null>(null);
  const [failed, setFailed] = useState(false);
  const reload = useCallback(async () => {
    if (!enabled) return;
    const got = await listTableCharacters(campaignId, sceneId);
    setFailed(got === null);
    setList(got ?? []);
  }, [enabled, campaignId, sceneId]);
  // Who is here, and the characters each has placed live, as the table sends them: a change re-reads the list.
  const here = (table?.members ?? []).filter((m) => m.present).map((m) => `${m.id}:${m.characters.map((c) => c.id).sort().join('+')}`).sort().join(',');
  useEffect(() => { void reload(); }, [reload, here]);
  useEffect(() => {
    if (!enabled) return undefined;
    const seen = () => document.visibilityState === 'visible';
    const onShow = () => { if (seen()) void reload(); };
    const timer = window.setInterval(onShow, TABLE_CHARACTERS_EVERY_MS);
    document.addEventListener('visibilitychange', onShow);
    window.addEventListener('focus', onShow);
    return () => { window.clearInterval(timer); document.removeEventListener('visibilitychange', onShow); window.removeEventListener('focus', onShow); };
  }, [enabled, reload]);
  return { list, failed, reload };
}

/** Say what the server said: a notice that came with success, or the refusal's sentence. True when it went through. */
function useSay(): (r: CharacterAnswer, ok?: string) => boolean {
  const { setNotice } = useTableState();
  return (r, ok) => {
    if (r.kind === 'ok') { if (r.notice) setNotice(r.notice); else if (ok) setNotice(ok); return true; }
    setNotice(r.kind === 'refused' ? r.reason : 'That could not be done right now.');
    return false;
  };
}

/**
 * The Library panel's section, the DM's alone: each character placed at this table, its player, and Place. Grouped
 * under their parties' names in the site's order (issue 1579), a character in two parties under each; a campaign with
 * one party has no group to show, and the list looks as it did.
 */
export function TableCharactersSection() {
  const { campaignId, table } = useShell();
  const { viewingAs, store, scene } = useTableState();
  const boardMap = useBoardMap();
  // The characters' routes are the `tokens` power's (issue 1160).
  const isDm = useHolds()('tokens') && !viewingAs;
  const { list, failed, reload } = useTableCharacters(isDm);
  const say = useSay();
  const [busy, setBusy] = useState<string | null>(null);
  const [plainAsk, setPlainAsk] = useState<{ character: TableCharacter; token: string; name: string } | null>(null);
  if (!isDm) return null;

  const objects = Object.values(store.state.objects) as TableObject[];
  // Linked means a token here that stands for the character AND its player owns (issue 1446): one a leave pruned is not.
  const linkedHere = (c: TableCharacter) => objects.some((o) => characterOf(o) === c.id && o.owners.includes(c.owner.id)) || c.linked.some((id) => id in store.state.objects);

  const place = async (c: TableCharacter, askFirst = true) => {
    // A plain token its player already has here (qa-runs 1255): link it rather than stand a second beside it, if the DM says.
    const plain = askFirst ? objects.find((o) => o.kind === 'token' && !characterOf(o) && !o.entry && o.owners.includes(c.owner.id)) : undefined;
    if (plain && !linkedHere(c)) { setPlainAsk({ character: c, token: plain.id, name: plain.name }); return; }
    setPlainAsk(null);
    const extent = boardExtent(boardMap);
    const centre = boardBus.viewCentre() ?? { x: EMPTY_GRID.squarePx * 2.5, y: EMPTY_GRID.squarePx * 2.5 };
    // The nearest free square to the view's centre, as the Compendium's Place seeks one: never on a token already there.
    const at = placeSpot(store.state.grid, extent, centre, true, objects.map((o) => ({ x: o.x, y: o.y })), true);
    setBusy(c.id);
    const r = await placeCharacter(campaignId, c.id, at, scene?.id ?? null);
    setBusy(null);
    say(r);
    await reload();
  };
  const linkPlain = async (c: TableCharacter, token: string) => {
    setPlainAsk(null);
    setBusy(c.id);
    const r = await linkCharacter(campaignId, token, c.id, scene?.id ?? null);
    setBusy(null);
    say(r);
    await reload();
  };

  return (
    <section className="space-y-1 rounded border border-stone-700 p-2" data-testid="table-characters" aria-label="Characters at this table">
      <h3 className="vtt-section-head">Characters at this table</h3>
      {list === null && <p className="text-xs text-stone-500">Loading…</p>}
      {failed && <p className="text-xs text-amber-300">The characters could not be read right now.</p>}
      {list !== null && !failed && list.length === 0 && (
        <p className="text-xs text-stone-500" data-testid="no-characters">No character is placed at this table yet. A player places one from their characters page on the site.</p>
      )}
      {byParty(list ?? [], table?.parties ?? []).map((g) => (
        <div key={g.party?.id ?? 'all'} data-testid={g.party ? `table-characters-party-${g.party.id}` : undefined}>
          {g.party && <h4 className="vtt-section-head pt-1">{g.party.name}</h4>}
          <ul className="divide-y divide-stone-800">
            {g.items.map((c) => (
              <li key={c.id} className="flex flex-wrap items-center gap-2 py-1" data-testid={`table-character-${c.id}`}>
                <span className="font-medium">{c.name}</span>
                <span className="text-xs text-stone-500">{c.owner.handle ? `@${c.owner.handle}` : 'a player'}</span>
                {linkedHere(c) && <span className="rounded bg-stone-800 px-1.5 text-xs text-stone-300" data-testid="linked">Linked</span>}
                <button type="button" className={`${btn} ml-auto`} disabled={busy === c.id} aria-label={`Place ${c.name} on the table`} onClick={() => void place(c)}>Place</button>
                {plainAsk?.character.id === c.id && (
                  <span className="flex w-full flex-wrap items-center gap-1 text-xs text-amber-200" role="group" aria-label={`${c.name} and ${plainAsk.name}`} data-testid="place-plain-ask">
                    <span>{c.owner.handle ? `@${c.owner.handle}` : 'Its player'} already has a plain token here, {plainAsk.name}.</span>
                    <button type="button" className={btn} data-testid="place-plain-link" onClick={() => void linkPlain(c, plainAsk.token)}>Link {plainAsk.name}</button>
                    <button type="button" className={btn} data-testid="place-plain-new" onClick={() => void place(c, false)}>Place another</button>
                    <button type="button" className={btn} onClick={() => setPlainAsk(null)}>Cancel</button>
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </section>
  );
}

/**
 * The way to a token's sheet (issue 1053), or null: "View sheet" for the DM on
 * any token linked to a character, "Open my sheet" for a player among its owners.
 * A token with no character, or anyone else's view, gets neither.
 */
export function sheetAction(o: unknown, isDm: boolean): { label: 'View sheet' | 'Open my sheet'; testid: 'view-sheet' | 'open-my-sheet'; character: string } | null {
  const character = characterOf(o);
  if (!character) return null;
  if (isDm) return { label: 'View sheet', testid: 'view-sheet', character };
  return (o as { mine?: unknown }).mine === true ? { label: 'Open my sheet', testid: 'open-my-sheet', character } : null;
}

/** The site's page for the sheet, in the table's one sheet window (issue 1053). `menu` makes it the token menu's item. */
export function SheetLink({ o, isDm, className = btn, menu, onOpen }: { o: unknown; isDm: boolean; className?: string; menu?: boolean; onOpen?: () => void }) {
  const { campaignId } = useShell();
  const a = sheetAction(o, isDm);
  if (!a) return null;
  return (
    <a className={className} href={characterPageUrl(a.character)} target={sheetWindowName(campaignId)} role={menu ? 'menuitem' : undefined}
      data-testid={menu ? `token-menu-${a.testid}` : a.testid} onClick={onOpen}>{a.label}</a>
  );
}

/** The Properties panel's block for a token: what it stands for, for the DM and for its owner. */
export function TokenCharacter({ o, mode = 'full' }: { o: TableObject | { id: string; kind: string; mine?: boolean; character?: string }; mode?: LinkMode }) {
  const { campaignId, table } = useShell();
  const { viewingAs, scene, store } = useTableState();
  const isDm = useHolds()('tokens') && !viewingAs;
  const character = characterOf(o);
  const mine = 'mine' in o && o.mine === true;
  const say = useSay();
  const [choosing, setChoosing] = useState(false);
  // The checkbox at the top reads the table's characters only while choosing: the Character section's list is the one
  // kept fresh, so a selected token never polls twice.
  const { list, reload } = useTableCharacters(isDm && (mode !== 'match' || choosing));
  const [pick, setPick] = useState('');
  const [busy, setBusy] = useState(false);
  // Asking whether to replace the numbers typed on the token before the link goes on (issue 1612).
  const [asking, setAsking] = useState(false);
  const words = useNumberWords();
  // A character that already stands on the board (qa-runs 1255): asked before a second token stands for it.
  const [secondOf, setSecondOf] = useState<{ id: string; name: string } | null>(null);
  useEffect(() => { setChoosing(false); setPick(''); setAsking(false); setSecondOf(null); }, [o.id]);
  if (o.kind !== 'token') return null;
  const sceneId = isDm ? scene?.id ?? null : null;

  const run = async (call: () => Promise<CharacterAnswer>, ok?: (r: CharacterAnswer & { kind: 'ok' }) => string | undefined) => {
    setBusy(true);
    const r = await call();
    setBusy(false);
    if (say(r, r.kind === 'ok' && ok ? ok(r) : undefined) && isDm) await reload();
    return r;
  };
  const refreshed = (r: { changed?: boolean }) => (r.changed === false ? 'Already as the sheet has it.' : 'Read from the sheet.');
  const refresh = () => void run(() => refreshCharacter(campaignId, o.id, sceneId), refreshed);
  // Hit points not on the sheet (issue 1052; #850, Tom's A): the DM's change waits for the owner's Accept, or the DM's refresh.
  const offSheet = character !== null && hpOffSheetOf(o);
  const mark = offSheet ? <span className="rounded bg-amber-900/60 px-1 text-amber-200" data-testid="hp-off-sheet" title="The hit points on this token are not on the character's sheet.">Hit points {NOT_ON_SHEET}</span> : null;

  const companionMark = companionOf(o) && hpOffSheetOf(o) ? <span className="rounded bg-amber-900/60 px-1 text-amber-200" data-testid="hp-off-sheet" title="The hit points on this token are not on its master's sheet.">Hit points {NOT_ON_SHEET}</span> : null;
  const typed = typedNumbers(o as { values?: Values }, words);
  const link = (replace: boolean) => {
    void run(() => (replace ? linkCharacter(campaignId, o.id, pick, sceneId, undefined, true) : linkCharacter(campaignId, o.id, pick, sceneId))).then((r) => { setAsking(false); if (r.kind === 'ok') setChoosing(false); });
  };

  const unlink = () => void run(() => linkCharacter(campaignId, o.id, null, sceneId), () => 'The token no longer stands for a character.');
  const chooser = choosing && (
    <form className="flex flex-wrap items-center gap-1" onSubmit={(e) => {
      e.preventDefault();
      if (!pick) return;
      const other = Object.values(store.state.objects as Record<string, TableObject>).find((t) => t.id !== o.id && t.kind === 'token' && characterOf(t) === pick);
      if (other) { setSecondOf({ id: other.id, name: other.name }); return; }
      if (typed) { setAsking(true); return; }
      link(false);
    }}>
      <select aria-label="Character" className="rounded bg-stone-800 px-1 py-0.5" value={pick} onChange={(e) => setPick(e.target.value)}>
        <option value="">{list === null ? 'Loading…' : list.length ? 'Choose a character' : 'No character is placed at this table'}</option>
        {(list ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}{c.owner.handle ? ` (@${c.owner.handle})` : ''}</option>)}
      </select>
      <button type="submit" className={btn} disabled={!pick || busy}>Link</button>
      <button type="button" className={btn} onClick={() => setChoosing(false)}>Cancel</button>
    </form>
  );
  const second = choosing && secondOf && (
    <div className="flex flex-wrap items-center gap-1 text-amber-200" role="group" aria-label="Second token" data-testid="link-second-ask">
      <span>{(list ?? []).find((c) => c.id === pick)?.name ?? 'That character'} already stands on the board as {secondOf.name}.</span>
      <button type="button" className={btn} disabled={busy} data-testid="link-second-replace" onClick={() => { const was = secondOf.id; setSecondOf(null); void linkCharacter(campaignId, was, null, sceneId).then(() => { if (typed) setAsking(true); else link(false); }); }}>Replace {secondOf.name}</button>
      <button type="button" className={btn} disabled={busy} data-testid="link-second-keep" onClick={() => { setSecondOf(null); if (typed) setAsking(true); else link(false); }}>Keep both</button>
      <button type="button" className={btn} onClick={() => setSecondOf(null)}>Cancel</button>
    </div>
  );
  const question = choosing && asking && <ReplaceQuestion typed={typed} busy={busy} onReplace={() => link(true)} onKeep={() => setAsking(false)} />;

  // The DM's *Match to a table character* at the top of Properties (issue 1612, Tom's A): ticked opens the choice,
  // unticked unlinks. The token keeps following the sheet; Refresh and the rest stay in the Character section.
  if (mode === 'match') {
    if (!isDm) return null;
    return (
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs" data-testid="match-character">
        <label className="flex items-center gap-1">
          <input type="checkbox" checked={character !== null || choosing} disabled={busy}
            onChange={(e) => { if (e.target.checked) setChoosing(true); else if (character) unlink(); else { setChoosing(false); setAsking(false); } }} />
          Match to a table character
        </label>
        {!character && chooser}
        {!character && question}
      </div>
    );
  }

  if (isDm) {
    if (character) {
      const placed = list?.find((c) => c.id === character);
      const name = placed?.name;
      // The token's party is its character's (issue 1579), read as it is shown: the DM's list says it.
      const party = partyWords(placed?.parties);
      return (
        <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="token-character">
          <span className="text-stone-300">Stands for {name ?? (list === null ? '…' : 'a character no longer at this table')}</span>
          {party && <span className="text-stone-400" data-testid="token-party">{party}</span>}
          {mark}
          <button type="button" className={btn} disabled={busy} onClick={refresh}>Refresh from the sheet</button>
          <button type="button" className={btn} disabled={busy} onClick={unlink}>Unlink</button>
          <SheetLink o={o} isDm />
          <TokenCompanions master={o} character={character} />
        </div>
      );
    }
    // A companion's token (qa-runs#384): one row of its master's sheet, read from it and saved back to it.
    if (companionOf(o)) {
      return (
        <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="token-companion">
          <span className="text-stone-300">Stands for a companion on its master's sheet</span>
          {companionMark}
          <button type="button" className={btn} disabled={busy} onClick={refresh}>Refresh from the sheet</button>
        </div>
      );
    }
    if (mode === 'block') return <p className="text-xs text-vtt-muted" data-testid="token-character">Not matched to a character. Tick <em>Match to a table character</em> above.</p>;
    return (
      <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="token-character">
        {!choosing && <button type="button" className={btn} onClick={() => { setChoosing(true); void reload(); }}>Link to a character…</button>}
        {chooser}
        {second}{question}
      </div>
    );
  }

  if (!character || !mine) return null;
  // A player's own character, from the members list as they are sent it: its parties are theirs (issue 1579).
  const own = partyWords(table?.members.find((m) => m.id === table.me.id)?.characters.find((c) => c.id === character)?.parties);
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="token-character">
      <span className="text-stone-300">Your character</span>
      {own && <span className="text-stone-400" data-testid="token-party">{own}</span>}
      {mark}
      {offSheet && !viewingAs && (
        <button type="button" className={btn} disabled={busy} title="Put the hit points on this token on your character's sheet."
          onClick={() => void run(() => acceptCharacterHp(campaignId, o.id), () => 'Your sheet has the hit points now.')}>Accept</button>
      )}
      {!viewingAs && <button type="button" className={btn} disabled={busy} onClick={refresh}>Refresh from the sheet</button>}
      <SheetLink o={o} isDm={false} />
      {!viewingAs && <TokenCompanions master={o} character={character} />}
    </div>
  );
}

/** The companion a thing in this person's projection stands for (qa-runs#384): the DM's alone to be sent. */
export const companionOf = (o: unknown): { character: string; row: string } | null => {
  const c = (o as { companion?: { character?: unknown; row?: unknown } } | null)?.companion;
  return c && typeof c.character === 'string' && typeof c.row === 'string' ? { character: c.character, row: c.row } : null;
};

/**
 * A master's companions (qa-runs#384) under its token's character block, for its player and the DM: each familiar,
 * animal companion or mount on the sheet, and Place, which puts its token on the nearest free square beside the master.
 * Read when asked for, never polled: the sheet is the site's.
 */
export function TokenCompanions({ master, character }: { master: { id: string; x?: number; y?: number }; character: string }) {
  const { campaignId } = useShell();
  const { store, scene, setNotice } = useTableState();
  const boardMap = useBoardMap();
  const say = useSay();
  const [list, setList] = useState<TableCompanion[] | null>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const sceneId = scene?.id ?? null;
  useEffect(() => { setOpen(false); setList(null); }, [master.id]);
  const load = async () => {
    const got = await listCompanions(campaignId, character, sceneId);
    if (got === null || !Array.isArray(got)) { setNotice(got?.reason ?? "The sheet's companions could not be read right now."); setList([]); return; }
    setList(got);
  };
  const place = async (c: TableCompanion) => {
    const objects = Object.values(store.state.objects) as TableObject[];
    const px = EMPTY_GRID.squarePx;
    const centre = { x: (master.x ?? px * 2) + px, y: master.y ?? px * 2 };
    const at = placeSpot(store.state.grid, boardExtent(boardMap), centre, true, objects.map((o) => ({ x: o.x, y: o.y })), true);
    setBusy(c.row);
    const r = await placeCompanion(campaignId, character, c.row, at, sceneId);
    setBusy(null);
    say(r, `${c.name} is on the table.`);
    await load();
  };
  if (!open) return <button type="button" className={btn} data-testid="companions-open" onClick={() => { setOpen(true); void load(); }}>Companions…</button>;
  return (
    <div className="w-full space-y-1" data-testid="token-companions" aria-label="Companions">
      {list === null && <p className="text-stone-500">Loading…</p>}
      {list !== null && list.length === 0 && <p className="text-stone-500" data-testid="no-companions">No familiar, companion or mount on this sheet.</p>}
      <ul className="divide-y divide-stone-800">
        {(list ?? []).map((c) => (
          <li key={c.row} className="flex flex-wrap items-center gap-2 py-1" data-testid={`companion-${c.row}`}>
            <span className="font-medium">{c.name}</span>
            {c.kind && <span className="text-stone-500">{c.kind}</span>}
            {c.linked.some((id) => id in store.state.objects) && <span className="rounded bg-stone-800 px-1.5 text-stone-300" data-testid="linked">On the table</span>}
            <button type="button" className={`${btn} ml-auto`} disabled={busy === c.row} aria-label={`Place ${c.name} on the table`} onClick={() => void place(c)}>Place</button>
          </li>
        ))}
      </ul>
      <button type="button" className={btn} onClick={() => setOpen(false)}>Close</button>
    </div>
  );
}
